import React from "react";
import { Box, Center, Spinner, Text, VStack, keyframes } from "@chakra-ui/react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@context/AuthContext";
import { colors } from "@theme/tokens";
import ScrollToTop from "@ui/atoms/AtomScrollToTop";

// CSS animation for page transitions
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
`;

/**
 * ProtectedAdminLayout
 * Layout for admin-only pages
 * Redirects to /signin if not authenticated
 * Redirects to /profile if authenticated but not admin
 */
function ProtectedAdminLayout() {
  const { isAuthenticated, isSessionLoading, isAdmin, user } = useAuth();
  const location = useLocation();

  // Show loading spinner while checking session
  if (isSessionLoading) {
    return (
      <Center minH="100vh" bg={colors.background.darkPrimary} color={colors.text.primary}>
        <VStack spacing={4} align="center">
          <Spinner size="lg" thickness="4px" color={colors.brand.primary} />
          <Text fontSize="md" opacity={0.8}>
            Проверяем ваши права доступа...
          </Text>
        </VStack>
      </Center>
    );
  }

  // Redirect to signin if not authenticated
  if (!isAuthenticated || !user) {
    return <Navigate to="/signin" replace state={{ from: location }} />;
  }

  // Redirect to profile if not admin
  if (!isAdmin) {
    return (
      <Navigate
        to="/profile"
        replace
        state={{
          error: "У вас нет доступа к админ-панели"
        }}
      />
    );
  }

  // Render admin panel for authorized admins
  return (
    <Box position="relative" bg={colors.background.darkPrimary} w="100%" minH="100vh">
      <ScrollToTop />

      {/* Main admin content */}
      <Box
        as="main"
        position="relative"
        zIndex={1}
        key={location.pathname}
        animation={`${fadeIn} 0.25s ease-out forwards`}
        sx={{ "@media (prefers-reduced-motion: reduce)": { animation: "none" } }}
      >
        <Outlet />
      </Box>
    </Box>
  );
}

export default ProtectedAdminLayout;
